import { fromHsl, luminance, mix, offset, toHsl } from '../lib/color';
import type { Border, Decoration, Design, Note, Pattern } from '../storage/schemas';
import { BUILT_IN_DESIGNS, DEFAULT_DESIGN_ID, getBuiltInDesign } from './catalog';

/** Everything needed to paint a note, after applying per-note overrides to its design. */
export interface Look {
  paper: string;
  line: string;
  ink: string;
  accent: string;
  pattern: Pattern;
  patternScale: number;
  font: string;
  paperAlpha?: number;
  lineAlpha?: number;
  border?: Border;
  decoration?: Decoration;
}

export function findDesign(id: string, custom: Design[]): Design {
  return custom.find((d) => d.id === id) ?? getBuiltInDesign(id) ?? getBuiltInDesign(DEFAULT_DESIGN_ID) ?? BUILT_IN_DESIGNS[0];
}

export function designLook(d: Design): Look {
  return {
    paper: d.paper,
    line: d.line,
    ink: d.ink,
    accent: d.accent,
    pattern: d.pattern,
    patternScale: d.patternScale,
    font: d.font,
    paperAlpha: d.paperAlpha,
    lineAlpha: d.lineAlpha,
    border: d.border,
    decoration: d.decoration,
  };
}

export function resolveLook(note: Pick<Note, 'designId' | 'style'>, custom: Design[]): Look {
  const base = designLook(findDesign(note.designId, custom));
  const s = note.style;
  return {
    ...base,
    paper: s.paper ?? base.paper,
    line: s.line ?? base.line,
    ink: s.ink ?? base.ink,
    accent: s.accent ?? base.accent,
    pattern: s.pattern ?? base.pattern,
    font: s.font ?? base.font,
  };
}

/** CSS colour for the paper, honouring translucent ("Clear") designs. */
export function paperCss(look: Pick<Look, 'paper' | 'paperAlpha'>): string {
  if (look.paperAlpha === undefined || look.paperAlpha >= 1) return look.paper;
  return `${look.paper}${Math.round(look.paperAlpha * 255).toString(16).padStart(2, '0')}`;
}

export function lineCss(look: Pick<Look, 'line' | 'lineAlpha'>): string {
  if (look.lineAlpha === undefined || look.lineAlpha >= 1) return look.line;
  return `${look.line}${Math.round(look.lineAlpha * 255).toString(16).padStart(2, '0')}`;
}

/** App-chrome colours derived from the note on screen, using rules measured from the reference app. */
export interface Chrome {
  bg: string;
  fg: string;
  icon: string;
  rim: string;
  sheetBg: string;
  sheetFg: string;
  dark: boolean;
}

/** Paper that the chrome is derived from; translucent designs sit on a deep navy like the reference gallery. */
function chromePaper(look: Look): string {
  if (look.paperAlpha !== undefined && look.paperAlpha < 1) return mix('#22314F', look.paper, look.paperAlpha);
  return look.paper;
}

export function chromeFor(look: Look): Chrome {
  const paper = chromePaper(look);
  const light = luminance(paper) > 0.42;
  // Measured: blue #367ED4 → background #2C74CA (−10 per channel), icons #68B0FF (+50 per channel);
  // yellow #F1D100 → icons #987E00 (≈ 37% towards black).
  const bg = offset(paper, -10);
  const icon = light ? mix(paper, '#000000', 0.37) : offset(paper, 50);
  const [h, s, l] = toHsl(paper);
  const rim = fromHsl(h >= 150 && h <= 300 ? h - 18 : h + 4, Math.min(100, s + 25), Math.min(light ? 52 : 55, l + 8));
  // Measured: settings sheet on #367ED4 paper is #4072C4 with #6EA3EF text.
  const sheetBg = light ? mix(paper, '#FFFFFF', 0.3) : fromHsl(h + 5, s * 0.8, l);
  const sheetFg = light ? mix(paper, '#000000', 0.55) : mix(icon, '#FFFFFF', 0.08);
  return { bg, fg: light ? '#171717' : '#FFFFFF', icon, rim, sheetBg, sheetFg, dark: !light };
}
